import { useContext } from 'react';

import { useLingui } from '@lingui/react/macro';
import { TableCell, TableRow } from '@repo/videa-components/src/ui/table';
import classNames from 'classnames';

import { ToothSurfaceMeasurementCell } from './ToothSurfaceMeasurementCell';
import { PerioChartModeContext } from './types';
import type {
  Arch,
  GingivalMarginCoronalDisplayMode,
  PerioChartTooth,
  ToothSide,
  ToothSurfaceMeasurement,
} from './types';

const MEASUREMENT_TYPES_IN_ORDER: ToothSurfaceMeasurement[] = [
  'probingDepth',
  'gingivalMargin',
  'clinicalAttachmentLevel',
  'mucogingivalJunction',
  'furcationGrade',
  'hasBleeding',
  'hasSuppuration',
];

interface Props {
  arch: Arch;
  enableDecodedGingivalMargin?: boolean;
  gingivalMarginCoronalDisplayMode?: GingivalMarginCoronalDisplayMode;
  side: ToothSide;
  teeth: PerioChartTooth[];
}

export const ToothSurfaceMeasurements = ({
  arch,
  enableDecodedGingivalMargin,
  gingivalMarginCoronalDisplayMode,
  side,
  teeth,
}: Props) => {
  const { t } = useLingui();
  const mode = useContext(PerioChartModeContext);

  const isFacial = side === 'facial';
  const isUpperFacial = arch === 'upper' && side === 'facial';
  const isLowerLingual = arch === 'lower' && side === 'lingual';
  const displayMeasurementTypes =
    isUpperFacial || isLowerLingual
      ? MEASUREMENT_TYPES_IN_ORDER
      : [...MEASUREMENT_TYPES_IN_ORDER].reverse();

  const getMeasurementLabel = (type: ToothSurfaceMeasurement): string => {
    switch (type) {
      case 'probingDepth':
        return t`Probing Depth`;
      case 'gingivalMargin':
        return t`Gingival Margin`;
      case 'clinicalAttachmentLevel':
        return t`Clinical Attachment Level`;
      case 'mucogingivalJunction':
        return t`Mucogingival Junction`;
      case 'furcationGrade':
        return t`Furcation Grade`;
      case 'hasBleeding':
        return t`Bleeding`;
      case 'hasSuppuration':
        return t`Suppuration`;
      default:
        return '';
    }
  };

  return (
    <>
      {displayMeasurementTypes.map((type) => (
        <TableRow key={type}>
          <TableCell
            className={classNames(
              'vai-text-[12px] !vai-py-[2px] !vai-px-2 vai-border vai-border-zinc-700',
              {
                'vai-text-zinc-300': mode === 'dark',
                'vai-text-zinc-950': mode === 'light',
              },
            )}
          >
            {getMeasurementLabel(type)}
          </TableCell>

          {teeth.map((tooth) => {
            const toothSurfaces = isFacial
              ? tooth.facialMeasurements
              : tooth.lingualMeasurements;
            return (
              <ToothSurfaceMeasurementCell
                enableDecodedGingivalMargin={enableDecodedGingivalMargin}
                gingivalMarginCoronalDisplayMode={
                  gingivalMarginCoronalDisplayMode
                }
                key={tooth.toothNumber}
                toothNumber={tooth.toothNumber}
                toothSurfaces={toothSurfaces}
                type={type}
              />
            );
          })}
        </TableRow>
      ))}
    </>
  );
};
